import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Btn, Input, Textarea, Select, Label, IconBtn } from "./ui-kit";
import { TASK_STATUSES, toISODate } from "@/lib/format";

const emptyTask = {
  name: "",
  description: "",
  date_started: "",
  deadline: "",
  cost_type: "single",
  cost: "",
  cost_min: "",
  cost_max: "",
  status: "Not Started",
  notes: "",
  task_type: "",
  supplier_cost: "",
  supplier_id: "",
};

const TaskModal = ({ open, onClose, onSave, task, title = "Add New Task", subtitle, statuses = TASK_STATUSES, allowRange = true, suppliers = [] }) => {
  const [form, setForm] = useState(emptyTask);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(task ? {
        ...emptyTask,
        ...task,
        cost: task.cost ?? "",
        cost_min: task.cost_min ?? "",
        cost_max: task.cost_max ?? "",
        supplier_cost: task.supplier_cost ?? "",
        supplier_id: task.supplier_id ?? "",
        date_started: task.date_started ? task.date_started.slice(0, 10) : "",
        deadline: task.deadline ? task.deadline.slice(0, 10) : "",
      } : emptyTask);
    }
  }, [open, task]);

  if (!open) return null;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description,
        date_started: toISODate(form.date_started),
        deadline: toISODate(form.deadline),
        cost_type: form.cost_type,
        cost: form.cost_type === "single" ? parseFloat(form.cost || 0) : 0,
        cost_min: form.cost_type === "range" ? parseFloat(form.cost_min || 0) : 0,
        cost_max: form.cost_type === "range" ? parseFloat(form.cost_max || 0) : 0,
        status: form.status,
        notes: form.notes,
        task_type: form.task_type || "",
        supplier_cost: form.task_type === "Print" ? parseFloat(form.supplier_cost || 0) : 0,
        supplier_id: form.task_type === "Print" ? (form.supplier_id || "") : "",
      };
      await onSave(payload);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="task-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="absolute inset-0 bg-zinc-950/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between px-6 pt-6 pb-2">
          <div>
            <h2 className="font-display text-2xl font-semibold text-zinc-950">{title}</h2>
            {subtitle && <p className="text-sm text-zinc-500 mt-1">{subtitle}</p>}
          </div>
          <IconBtn onClick={onClose} data-testid="task-modal-close"><X className="w-4 h-4" /></IconBtn>
        </div>

        <form onSubmit={handleSubmit} className="px-6 pb-6 pt-4 space-y-4">
          <div>
            <Label required>Task Name</Label>
            <Input
              data-testid="task-name-input"
              required
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="e.g. Print of 100x A7 Tent Cards"
            />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              data-testid="task-desc-input"
              rows={3}
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              placeholder="Details about the task..."
            />
          </div>
          <div>
            <Label>Task Type</Label>
            <div className="flex items-center gap-2 text-xs">
              {[
                { value: "", label: "—" },
                { value: "Design", label: "Design" },
                { value: "Print", label: "Print" },
              ].map((opt) => (
                <button
                  key={opt.value || "none"}
                  type="button"
                  data-testid={`task-type-${opt.value || "none"}`}
                  onClick={() => update("task_type", opt.value)}
                  className={`px-3 py-1.5 rounded-full border transition-colors ${
                    form.task_type === opt.value
                      ? "bg-zinc-950 text-white border-zinc-950"
                      : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Date Started</Label>
              <Input type="date" data-testid="task-start-input" value={form.date_started} onChange={(e) => update("date_started", e.target.value)} />
            </div>
            <div>
              <Label>Deadline</Label>
              <Input type="date" data-testid="task-deadline-input" value={form.deadline} onChange={(e) => update("deadline", e.target.value)} />
            </div>
          </div>

          {form.task_type === "Print" && (
            <div className="bg-amber-50/60 border border-amber-200 rounded-xl p-4 space-y-3" data-testid="markup-section">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
                <div>
                  <Label>Suppliers cost (£)</Label>
                  <Input
                    data-testid="supplier-cost-input"
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.supplier_cost}
                    onChange={(e) => update("supplier_cost", e.target.value)}
                    placeholder="e.g. 120.00"
                  />
                </div>
                <div>
                  <Label>Markup</Label>
                  <div className="flex items-center gap-2">
                    {[10, 20, 30].map((pct) => (
                      <button
                        key={pct}
                        type="button"
                        data-testid={`markup-${pct}`}
                        onClick={() => {
                          const supp = parseFloat(form.supplier_cost || 0);
                          if (!supp) return;
                          const marked = Math.round(supp * (1 + pct / 100) * 100) / 100;
                          update("cost_type", "single");
                          update("cost", marked.toFixed(2));
                        }}
                        disabled={!parseFloat(form.supplier_cost || 0)}
                        className="px-3 py-2 rounded-full border text-xs font-medium transition-colors bg-white text-zinc-700 border-zinc-200 hover:border-amber-500 hover:bg-amber-100 hover:text-amber-900 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-zinc-200 disabled:hover:bg-white"
                      >
                        +{pct}%
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <Label>Supplier</Label>
                <Select
                  data-testid="task-supplier-select"
                  value={form.supplier_id}
                  onChange={(e) => update("supplier_id", e.target.value)}
                >
                  <option value="">— None —</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </Select>
              </div>
              <p className="text-[11px] text-amber-800/80">
                Markup buttons calculate the price for the customer from your supplier&rsquo;s quote — kept internal, never shown on the client PDF.
              </p>
            </div>
          )}

          {allowRange && (
            <div className="flex items-center gap-2 text-xs">
              <button
                type="button"
                data-testid="cost-mode-single"
                onClick={() => update("cost_type", "single")}
                className={`px-3 py-1.5 rounded-full border transition-colors ${form.cost_type === "single" ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-200"}`}
              >Fixed price</button>
              <button
                type="button"
                data-testid="cost-mode-range"
                onClick={() => update("cost_type", "range")}
                className={`px-3 py-1.5 rounded-full border transition-colors ${form.cost_type === "range" ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-200"}`}
              >Range (£ min – £ max)</button>
            </div>
          )}

          {form.cost_type === "single" ? (
            <div>
              <Label>Price to customer (£)</Label>
              <Input
                data-testid="task-cost-input"
                type="number"
                step="0.01"
                min="0"
                value={form.cost}
                onChange={(e) => update("cost", e.target.value)}
              />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Price to customer min (£)</Label>
                <Input
                  data-testid="task-cost-min-input"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.cost_min}
                  onChange={(e) => update("cost_min", e.target.value)}
                />
              </div>
              <div>
                <Label>Price to customer max (£)</Label>
                <Input
                  data-testid="task-cost-max-input"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.cost_max}
                  onChange={(e) => update("cost_max", e.target.value)}
                />
              </div>
            </div>
          )}

          <div>
            <Label required>Status</Label>
            <Select data-testid="task-status-select" value={form.status} onChange={(e) => update("status", e.target.value)}>
              {statuses.map((s) => <option key={s}>{s}</option>)}
            </Select>
          </div>

          <div>
            <Label>Notes</Label>
            <Textarea
              data-testid="task-notes-input"
              rows={2}
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              placeholder="Add any additional notes or details..."
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Btn type="button" variant="secondary" onClick={onClose} data-testid="task-cancel-btn">Cancel</Btn>
            <Btn type="submit" disabled={saving} data-testid="task-save-btn">{task ? "Save Task" : "Add Task"}</Btn>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TaskModal;
